// src/data/letterhead.js
//
// THE PER-COMMITTEE LETTERHEAD - Bob, 2026-09-25: "I'd like to offer the General Chairmen the
// opportunity to create their own letterhead and signature sign-off." Ruling 229 (under PD-19,
// withdrawable by number): a letterhead is per GENERAL COMMITTEE (GO-342 vs GO-341 identity), set by
// the General Chairperson of that committee, and it is a PRESENTATION layer - it never converts a
// machine draft into authority. The officer's sign-off does that (src\data\signoff.js, ruling 231).
//
// WHERE IT LIVES, AND WHY NOT ON THE SPINE. offices/ and officeTerms/ are the append-only succession
// record, written ONLY by the Admin SDK (firestore.rules: allow write: if false on both). A GC cannot
// self-serve a write there and should not - a term is a fact about who held authority, not a place for
// an officer's stationery. committees/{committeeId} already carries the ONE client self-update a GC is
// trusted with: the continuity-plan choice (firestore.rules, 6a-11), guarded by inGCScope(committeeId)
// + hasOnly + recordedBy == uid. A letterhead is the same shape of act - the GC of THIS committee
// records ONE field on his own committee's document - so it goes there by exactly that pattern. The
// committee id IS the general-committee identity Bob named.
//
// HONESTY (PD-0, PD-4). A letterhead is the officer's OWN entered identity - his committee's name,
// address and identifiers - the same kind of self-attested fact as his profile. It is HIS statement,
// not the platform's finding, and nothing here is sourced from a catalogue or inferred. It renders
// ABOVE the resolved office-of-record header (src\data\letterGroup.js), which still carries, unchanged,
// the who-holds-authority truth read from the spine. The letterhead adds a banner; it hides nothing and
// replaces nothing that was sourced.
//
// A LOGO IS A POINTER, NOT INK IN A TEXT LETTER. The letters this platform generates are plain text
// (copied and downloaded as .txt). A text letter cannot embed an image, so renderLetterheadBanner()
// returns TEXT ONLY and never claims a logo is present in the text. The logo image is a Storage object
// scoped to the committee's GC and is shown in the on-screen preview by the panel - it is not asserted
// into the body of a text letter, because that would be asserting something the text does not contain.
//
// Pure module: no React, no Firestore. The panel reads committees/{id}.letterhead and hands it in.

const F = Object.freeze;

export const MAX_NAME_LEN = 140;
export const MAX_LINE_LEN = 120;
export const MAX_ADDRESS_LINES = 8;
export const MAX_IDENTIFIERS = 8;

// The fields a GC may set. `logoPath` is a Storage pointer, never bytes; it is optional.
export const LETTERHEAD_FIELDS = F(['committeeName', 'addressLines', 'identifiers', 'logoPath']);

function isStr(v) { return typeof v === 'string'; }
function nonEmpty(v) { return isStr(v) ? v.trim() !== '' : v !== null && v !== undefined && v !== ''; }
function clean(v) { return isStr(v) ? v.replace(/[\r\n]+/g, ' ').trim() : ''; }
function cleanLines(v, cap) {
  const arr = Array.isArray(v) ? v : (isStr(v) ? v.split(/\r?\n/) : []);
  return arr.map((x) => clean(String(x))).filter((s) => s !== '').slice(0, cap);
}

// A letterhead reduced to exactly its stored shape - trimmed, single-line entries, capped, no stray
// keys. `recordedBy` / `recordedAt` are the provenance the rule requires and the panel sets; they are
// carried through untouched when present so a round-trip does not drop them.
export function normalizeLetterhead(input) {
  const i = input || {};
  const out = {
    committeeName: clean(i.committeeName).slice(0, MAX_NAME_LEN),
    addressLines: cleanLines(i.addressLines, MAX_ADDRESS_LINES).map((s) => s.slice(0, MAX_LINE_LEN)),
    identifiers: cleanLines(i.identifiers, MAX_IDENTIFIERS).map((s) => s.slice(0, MAX_LINE_LEN)),
    logoPath: nonEmpty(i.logoPath) ? clean(i.logoPath) : null,
  };
  if (nonEmpty(i.recordedBy)) out.recordedBy = clean(i.recordedBy);
  if (nonEmpty(i.recordedAt)) out.recordedAt = String(i.recordedAt).trim();
  return out;
}

// True only when there is a real letterhead to render. An empty object or one with no committee name is
// NOT a letterhead - the generators must render exactly the header they render today when there is none.
export function hasLetterhead(lh) {
  return !!(lh && nonEmpty(lh.committeeName));
}

// Validation the panel shows and the rule mirrors. A letterhead with no committee name is refused: a
// banner with no name on it is not a letterhead, it is empty presentation, and it must not be saved as
// if it were one. Everything else is optional. Returns the normalized value so the caller stores what
// was validated, not the raw input.
export function validateLetterhead(input) {
  const value = normalizeLetterhead(input);
  const errors = [];
  if (!nonEmpty(value.committeeName)) errors.push('a letterhead carries the committee name');
  if (value.committeeName.length > MAX_NAME_LEN) errors.push('the committee name is at most ' + MAX_NAME_LEN + ' characters');
  if ((Array.isArray(input && input.addressLines) ? input.addressLines.length : 0) > MAX_ADDRESS_LINES) {
    errors.push('at most ' + MAX_ADDRESS_LINES + ' address lines');
  }
  if ((Array.isArray(input && input.identifiers) ? input.identifiers.length : 0) > MAX_IDENTIFIERS) {
    errors.push('at most ' + MAX_IDENTIFIERS + ' identifier lines');
  }
  return { ok: errors.length === 0, errors, value };
}

// Who may set a committee's letterhead: the General Chairperson of THAT committee, or the administrator
// (the committees document's own write rule allows admin). Mirrors continuityPlan.mayRecord, and the
// GC arm is exactly what inGCScope(committeeId) enforces in firestore.rules.
export function mayRecordLetterhead(user, committee) {
  if (!user || !committee) return false;
  if (user.isAdmin) return true;
  return user.role === 'general_chair' && user.committee === committee;
}

// The patch a GC writes. Returns { ok, errors, patch } - patch is { letterhead: {...} } and nothing
// else, so the rule that lets him touch ONLY that field is the same line as this shape. recordedBy is
// his uid, which the rule asserts, so the write cannot be attributed to anyone else.
export function letterheadRecord(user, committee, input, at) {
  const errors = [];
  if (!mayRecordLetterhead(user, committee)) {
    errors.push('Only the General Chairperson of ' + (committee || 'this committee') + ' sets its letterhead.');
  }
  const v = validateLetterhead(input);
  errors.push(...v.errors);
  if (errors.length) return { ok: false, errors, patch: null };
  return {
    ok: true, errors: [],
    patch: {
      letterhead: {
        ...v.value,
        recordedBy: (user && user.uid) || null,
        recordedByName: (user && (user.name || user.email)) || 'Unknown',
        recordedByRole: user && user.isAdmin ? 'admin' : 'general_chair',
        recordedAt: at || new Date().toISOString(),
      },
    },
  };
}

// The banner, TEXT ONLY, as it prints at the top of a letter. Committee name, then address lines, then
// identifier lines - each exactly as the GC entered it, nothing composed. Returns '' when there is no
// letterhead, so a caller can concatenate unconditionally and get today's header when none is set.
export function renderLetterheadBanner(lh) {
  if (!hasLetterhead(lh)) return '';
  const v = normalizeLetterhead(lh);
  const lines = [v.committeeName];
  v.addressLines.forEach((l) => lines.push(l));
  v.identifiers.forEach((l) => lines.push(l));
  return lines.join('\n');
}
